/**
 * "Will this fit my bike?" — a rule-based check of a candidate part's
 * standards (component.specs) against the parts fitted to a bike.
 *
 * Pure and deterministic: no AI and no ride data. Every answer comes with its
 * reasons, each naming the part it was checked against, so the rider can see
 * why. Missing standards give "can't tell", never a guess.
 */
import type { ComponentCategory, InstallPosition } from './enums'
import { componentCategoryLabel } from './labels'
import { specFields } from './specs'

export interface CompatPart {
  id?: string
  /** How the rider knows it, e.g. "Fox 36 Factory". */
  name: string
  category: ComponentCategory
  position: InstallPosition
  specs: Record<string, unknown>
}

export type CheckStatus = 'ok' | 'caution' | 'conflict' | 'unknown'
export type CompatVerdict = 'fits' | 'caution' | 'wont_fit' | 'unknown'

export interface CompatCheck {
  /** What was checked, e.g. "Rear axle". */
  topic: string
  status: CheckStatus
  message: string
  against: { id?: string; name: string } | null
}

export interface CompatResult {
  verdict: CompatVerdict
  checks: CompatCheck[]
}

type End = 'front' | 'rear'

/** Categories whose parts sit at one end of the bike. */
const POSITIONAL: ReadonlySet<ComponentCategory> = new Set([
  'wheel',
  'hub',
  'rim',
  'tyre',
  'inner_tube',
  'brake',
  'brake_pads',
  'brake_rotor',
])

/** Categories where front or rear changes the answer, for the UI to ask. */
export const needsPosition = (category: ComponentCategory) => POSITIONAL.has(category)

const FRONT_AXLE_VALUES = new Set([
  'Boost 15x110',
  '15x100',
  '20x110',
  '12x100',
  '9mm QR',
  '12x100 road',
])

/** Which end a part is at: fixed for frames and forks, else its install position. */
function endOf(part: CompatPart, fixed?: End): End | undefined {
  if (fixed) return fixed
  if (!POSITIONAL.has(part.category)) return undefined
  if (part.position === 'front' || part.position === 'rear') return part.position
  // A wheel or hub's axle says which end it belongs to.
  const axle = part.specs.axle
  if ((part.category === 'wheel' || part.category === 'hub') && typeof axle === 'string') {
    return FRONT_AXLE_VALUES.has(axle) ? 'front' : 'rear'
  }
  return undefined
}

interface Side {
  categories: readonly ComponentCategory[]
  key: string
  /** Frames are the rear end, forks the front; positional parts use their position. */
  end?: End
  /** Only parts at this end take part (e.g. freehubs are rear only). */
  only?: End
}

type Outcome = { status: Exclude<CheckStatus, 'unknown'>; message: string }

interface Rule {
  topic: string
  a: Side
  b: Side
  /** Compare the A-side value with the B-side value. Names are for messages. */
  compare: (a: unknown, b: unknown, names: { a: string; b: string }) => Outcome
}

// ---------------------------------------------------------------------------
// Comparisons

const ok = (message: string): Outcome => ({ status: 'ok', message })
const caution = (message: string): Outcome => ({ status: 'caution', message })
const conflict = (message: string): Outcome => ({ status: 'conflict', message })

const str = (v: unknown) => String(v)
const norm = (v: unknown) =>
  String(v)
    .toLowerCase()
    .replace(/[\s._-]+/g, '')

/** Same value on both sides, or it does not fit. */
const same =
  (what: string) =>
  (a: unknown, b: unknown, n: { a: string; b: string }): Outcome =>
    a === b
      ? ok(`${what} match: ${str(a)}.`)
      : conflict(`${n.a} is ${str(a)}, but ${n.b} is ${str(b)}.`)

/** Free-text standards: equal after tidying is a match, otherwise worth checking. */
const sameText =
  (what: string) =>
  (a: unknown, b: unknown, n: { a: string; b: string }): Outcome =>
    norm(a) === norm(b)
      ? ok(`${what} match: ${str(a)}.`)
      : caution(
          `${n.a} says ${str(a)} and ${n.b} says ${str(b)}. Check they are the same ${what.toLowerCase()}.`,
        )

/** Bead seat diameter, so 29" and 700c (both 622 mm) count as the same size. */
const BSD: Record<string, number> = {
  '29"': 622,
  '700c': 622,
  '27.5"': 584,
  '650b': 584,
  '26"': 559,
  '24"': 507,
  '20"': 406,
}
/**
 * The rim's bead seat diameter: from the standard sizes, or from a number in a
 * custom value ("ETRTO 60-622", "ISO 686"). Null when it can't be told.
 */
function beadSeat(v: unknown): number | null {
  const known = BSD[str(v)]
  if (known !== undefined) return known
  const m = /(?:^|[^\d])(\d{3})(?:$|[^\d])/.exec(str(v))
  const n = m ? Number(m[1]) : NaN
  return n >= 150 && n <= 800 ? n : null
}

const wheelSize = (a: unknown, b: unknown, n: { a: string; b: string }): Outcome => {
  const bsdA = beadSeat(a)
  const bsdB = beadSeat(b)
  if (bsdA !== null && bsdB !== null) {
    return bsdA === bsdB
      ? ok(`Wheel sizes match: ${str(a)}${a === b ? '' : ` and ${str(b)} are the same rim size`}.`)
      : conflict(`${n.a} is ${str(a)}, but ${n.b} is ${str(b)}.`)
  }
  // A non-standard size Klunkers can't read: the same words match; otherwise check.
  return norm(a) === norm(b)
    ? ok(`Wheel sizes match: ${str(a)}.`)
    : caution(
        `${n.a} says ${str(a)} and ${n.b} says ${str(b)}. Check they are the same wheel size.`,
      )
}

const mm = (v: unknown): number | null => {
  const m = /(\d+(?:\.\d+)?)/.exec(str(v))
  return m ? Number(m[1]) : null
}

/** A tube (the A side) that is smaller than the hole (B side) works with a shim. */
const shimmable =
  (what: string) =>
  (inner: unknown, outer: unknown, n: { a: string; b: string }): Outcome => {
    if (inner === outer) return ok(`${what} match: ${str(inner)}.`)
    const i = mm(inner)
    const o = mm(outer)
    if (
      i === null ||
      o === null ||
      str(inner).includes('proprietary') ||
      str(outer).includes('proprietary')
    ) {
      return conflict(`${n.a} is ${str(inner)}, but ${n.b} is ${str(outer)}.`)
    }
    if (i < o)
      return caution(`${n.a} is ${str(inner)} and ${n.b} is ${str(outer)}. It fits with a shim.`)
    return conflict(`${n.a} is ${str(inner)}, which is too big for ${n.b} (${str(outer)}).`)
  }

const freehub = (cassette: unknown, hub: unknown, n: { a: string; b: string }): Outcome => {
  if (cassette === hub) return ok(`Freehubs match: ${str(hub)}.`)
  if (cassette === 'SRAM XD' && hub === 'SRAM XDR') {
    return caution(`${n.a} is SRAM XD and ${n.b} is SRAM XDR. It fits with the 1.85 mm spacer.`)
  }
  return conflict(`${n.a} needs a ${str(cassette)} freehub, but ${n.b} has ${str(hub)}.`)
}

const speeds =
  (level: 'conflict' | 'caution') =>
  (a: unknown, b: unknown, n: { a: string; b: string }): Outcome => {
    if (a === b) return ok(`Both are ${str(a)}-speed.`)
    const msg = `${n.a} is ${str(a)}-speed, but ${n.b} is ${str(b)}-speed.`
    return level === 'conflict'
      ? conflict(msg)
      : caution(`${msg} Check the maker says they work together.`)
  }

/** Largest cog from a range such as "10-52". */
const largestCog = (range: unknown): number | null => {
  const nums = str(range).match(/\d+/g)?.map(Number)
  return nums?.length ? Math.max(...nums) : null
}
const maxCog = (
  derailleurMax: unknown,
  cassetteRange: unknown,
  n: { a: string; b: string },
): Outcome => {
  const cog = largestCog(cassetteRange)
  const max = Number(derailleurMax)
  if (cog === null) return caution(`Could not read the largest cog from "${str(cassetteRange)}".`)
  return cog <= max
    ? ok(`${n.a} takes up to ${max}t; the largest cog on ${n.b} is ${cog}t.`)
    : conflict(`${n.a} takes up to ${max}t, but ${n.b} goes up to ${cog}t.`)
}

const udh = (mount: unknown, hanger: unknown, n: { a: string; b: string }): Outcome => {
  if (mount !== 'SRAM UDH direct mount') return ok(`${n.a} uses a ${str(mount).toLowerCase()}.`)
  return /udh/i.test(str(hanger))
    ? ok(`${n.b} has a UDH hanger, which ${n.a} needs.`)
    : caution(
        `${n.a} mounts direct to a UDH frame. ${n.b} lists its hanger as "${str(hanger)}"; check it is UDH.`,
      )
}

const STEM_STEERER: Record<string, readonly string[]> = {
  '1-1/8" (28.6mm)': ['Tapered 1-1/8" to 1.5"', 'Straight 1-1/8"'],
  '1.5"': ['Straight 1.5"'],
  '1" (25.4mm)': ['1" threaded'],
}
const stemSteerer = (clamp: unknown, steerer: unknown, n: { a: string; b: string }): Outcome =>
  STEM_STEERER[str(clamp)]?.includes(str(steerer))
    ? ok(`${n.a} clamps a ${str(clamp)} steerer, which suits ${n.b} (${str(steerer)}).`)
    : conflict(`${n.a} clamps a ${str(clamp)} steerer, but ${n.b} is ${str(steerer)}.`)

const rotorMount = (rotor: unknown, hub: unknown, n: { a: string; b: string }): Outcome => {
  if (rotor === hub) return ok(`Rotor mounts match: ${str(hub)}.`)
  if (rotor === '6-bolt' && hub === 'Center Lock') {
    return caution(
      `${n.a} is 6-bolt and ${n.b} is Center Lock. It fits with a Center Lock adapter.`,
    )
  }
  return conflict(`${n.a} is ${str(rotor)}, but ${n.b} is ${str(hub)}.`)
}

const maxRotor = (diameter: unknown, max: unknown, n: { a: string; b: string }): Outcome =>
  Number(diameter) <= Number(max)
    ? ok(`${n.b} takes rotors up to ${str(max)} mm; ${n.a} is ${str(diameter)} mm.`)
    : conflict(`${n.a} is ${str(diameter)} mm, but ${n.b} takes rotors up to ${str(max)} mm.`)

/** "Post mount 180mm" → { type: 'post', base: 180 }. */
function brakeMount(v: unknown): { type: 'post' | 'flat' | 'is' | 'rim'; base: number | null } {
  const s = str(v)
  if (s.startsWith('Post')) return { type: 'post', base: mm(s) }
  if (s.startsWith('Flat')) return { type: 'flat', base: mm(s) }
  if (s.startsWith('IS')) return { type: 'is', base: null }
  return { type: 'rim', base: null }
}

const rotorOnMount = (diameter: unknown, mount: unknown, n: { a: string; b: string }): Outcome => {
  const d = Number(diameter)
  const m = brakeMount(mount)
  if (m.type === 'rim') return conflict(`${n.b} is for rim brakes, so it cannot take a disc rotor.`)
  if (m.type === 'is')
    return caution(`${n.b} is IS mount. A ${d} mm rotor needs the matching IS adapter.`)
  if (m.base === null) return caution(`Check ${n.b} takes a ${d} mm rotor.`)
  if (d === m.base) return ok(`${n.b} is made for a ${d} mm rotor.`)
  if (d < m.base)
    return conflict(
      `${n.b} is ${m.base} mm native, so the smallest rotor it takes is ${m.base} mm; ${n.a} is ${d} mm.`,
    )
  return caution(
    `${n.b} is ${m.base} mm native. A ${d} mm rotor needs a +${d - m.base} mm adapter.`,
  )
}

const caliperOnMount = (caliper: unknown, mount: unknown, n: { a: string; b: string }): Outcome => {
  const c = str(caliper)
  const m = brakeMount(mount)
  const cType = c.startsWith('Post') ? 'post' : c.startsWith('Flat') ? 'flat' : 'rim'
  if (cType === 'rim' || m.type === 'rim') {
    return cType === m.type
      ? ok(`Both are rim brake.`)
      : conflict(`${n.a} is a ${c.toLowerCase()} caliper, but ${n.b} is ${str(mount)}.`)
  }
  if (cType === m.type) return ok(`${n.a} and ${n.b} are both ${c.toLowerCase()}.`)
  if (cType === 'post' && m.type === 'is')
    return caution(`${n.b} is IS mount. The post mount caliper fits with an IS adapter.`)
  return caution(
    `${n.a} is ${c.toLowerCase()} but ${n.b} is ${str(mount)}. It needs an adapter, if one exists for this frame or fork.`,
  )
}

const tyreClearance = (width: unknown, max: unknown, n: { a: string; b: string }): Outcome =>
  Number(width) <= Number(max)
    ? ok(`${n.b} clears tyres up to ${str(max)} mm; ${n.a} is ${str(width)} mm.`)
    : conflict(`${n.a} is ${str(width)} mm, but ${n.b} clears tyres up to ${str(max)} mm.`)

const tubeless = (setup: unknown, ready: unknown, n: { a: string; b: string }): Outcome => {
  if (!str(setup).startsWith('Tubeless'))
    return ok(`${n.a} runs with a tube, so tubeless is not needed.`)
  return ready === true
    ? ok(`${n.b} is tubeless ready.`)
    : conflict(`${n.a} is set up tubeless, but ${n.b} is not tubeless ready.`)
}

const actuation = (a: unknown, b: unknown, n: { a: string; b: string }): Outcome =>
  a === b
    ? ok(`Both shift by ${str(a).toLowerCase()}.`)
    : conflict(`${n.a} is ${str(a)}, but ${n.b} is ${str(b)}.`)

// ---------------------------------------------------------------------------
// Rules. Each links a standard on one kind of part to one on another; the
// check runs whichever side the candidate is on.

const WHEELISH = ['wheel', 'rim', 'tyre', 'inner_tube'] as const
const WHEEL_OR_HUB = ['wheel', 'hub'] as const

const RULES: readonly Rule[] = [
  {
    topic: 'Wheel size',
    a: { categories: WHEELISH, key: 'wheel_size' },
    b: { categories: ['fork'], key: 'wheel_size', end: 'front' },
    compare: wheelSize,
  },
  {
    topic: 'Wheel size',
    a: { categories: WHEELISH, key: 'wheel_size' },
    b: { categories: ['frame'], key: 'wheel_size', end: 'rear' },
    compare: wheelSize,
  },
  {
    topic: 'Wheel size',
    a: { categories: ['tyre', 'inner_tube'], key: 'wheel_size' },
    b: { categories: ['wheel', 'rim'], key: 'wheel_size' },
    compare: wheelSize,
  },
  {
    topic: 'Front axle',
    a: { categories: WHEEL_OR_HUB, key: 'axle', only: 'front' },
    b: { categories: ['fork'], key: 'axle', end: 'front' },
    compare: same('Axles'),
  },
  {
    topic: 'Rear axle',
    a: { categories: WHEEL_OR_HUB, key: 'axle', only: 'rear' },
    b: { categories: ['frame'], key: 'rear_axle', end: 'rear' },
    compare: same('Axles'),
  },
  {
    topic: 'Freehub',
    a: { categories: ['cassette'], key: 'freehub', end: 'rear' },
    b: { categories: WHEEL_OR_HUB, key: 'freehub', only: 'rear' },
    compare: freehub,
  },
  {
    topic: 'Speeds',
    a: { categories: ['chain'], key: 'speeds' },
    b: { categories: ['cassette', 'derailleur_rear', 'shifter'], key: 'speeds' },
    compare: speeds('conflict'),
  },
  {
    topic: 'Speeds',
    a: { categories: ['cassette'], key: 'speeds' },
    b: { categories: ['derailleur_rear', 'shifter'], key: 'speeds' },
    compare: speeds('conflict'),
  },
  {
    topic: 'Speeds',
    a: { categories: ['derailleur_rear'], key: 'speeds' },
    b: { categories: ['shifter'], key: 'speeds' },
    compare: speeds('conflict'),
  },
  {
    topic: 'Speeds',
    a: { categories: ['chainring'], key: 'speeds' },
    b: { categories: ['chain'], key: 'speeds' },
    compare: speeds('caution'),
  },
  {
    topic: 'Largest cog',
    a: { categories: ['derailleur_rear'], key: 'max_cog' },
    b: { categories: ['cassette'], key: 'range' },
    compare: maxCog,
  },
  {
    topic: 'Derailleur mount',
    a: { categories: ['derailleur_rear'], key: 'mount' },
    b: { categories: ['frame'], key: 'derailleur_hanger' },
    compare: udh,
  },
  {
    topic: 'Shifting',
    a: { categories: ['shifter'], key: 'actuation' },
    b: { categories: ['derailleur_rear'], key: 'actuation' },
    compare: actuation,
  },
  {
    topic: 'Bottom bracket shell',
    a: { categories: ['bottom_bracket'], key: 'shell' },
    b: { categories: ['frame'], key: 'bb_shell' },
    compare: same('Shells'),
  },
  {
    topic: 'Crank spindle',
    a: { categories: ['crankset'], key: 'spindle' },
    b: { categories: ['bottom_bracket'], key: 'spindle' },
    compare: same('Spindles'),
  },
  {
    topic: 'Seatpost diameter',
    a: { categories: ['seatpost'], key: 'diameter' },
    b: { categories: ['frame'], key: 'seatpost_diameter' },
    compare: shimmable('Diameters'),
  },
  {
    topic: 'Bar clamp',
    a: { categories: ['handlebar'], key: 'clamp' },
    b: { categories: ['stem'], key: 'bar_clamp' },
    compare: shimmable('Clamps'),
  },
  {
    topic: 'Steerer',
    a: { categories: ['stem'], key: 'steerer_clamp' },
    b: { categories: ['fork'], key: 'steerer' },
    compare: stemSteerer,
  },
  {
    topic: 'Steerer',
    a: { categories: ['headset'], key: 'steerer' },
    b: { categories: ['fork'], key: 'steerer' },
    compare: same('Steerers'),
  },
  {
    topic: 'Headset',
    a: { categories: ['headset'], key: 'standard' },
    b: { categories: ['frame'], key: 'headset' },
    compare: sameText('Headset standard'),
  },
  {
    topic: 'Rotor mount',
    a: { categories: ['brake_rotor'], key: 'mount' },
    b: { categories: WHEEL_OR_HUB, key: 'rotor_mount' },
    compare: rotorMount,
  },
  {
    topic: 'Rotor size',
    a: { categories: ['brake_rotor'], key: 'diameter', only: 'front' },
    b: { categories: ['fork'], key: 'max_rotor', end: 'front' },
    compare: maxRotor,
  },
  {
    topic: 'Rotor size',
    a: { categories: ['brake_rotor'], key: 'diameter', only: 'rear' },
    b: { categories: ['frame'], key: 'max_rotor_rear', end: 'rear' },
    compare: maxRotor,
  },
  {
    topic: 'Brake mount',
    a: { categories: ['brake_rotor'], key: 'diameter', only: 'front' },
    b: { categories: ['fork'], key: 'brake_mount', end: 'front' },
    compare: rotorOnMount,
  },
  {
    topic: 'Brake mount',
    a: { categories: ['brake_rotor'], key: 'diameter', only: 'rear' },
    b: { categories: ['frame'], key: 'rear_brake_mount', end: 'rear' },
    compare: rotorOnMount,
  },
  {
    topic: 'Caliper mount',
    a: { categories: ['brake'], key: 'caliper_mount', only: 'front' },
    b: { categories: ['fork'], key: 'brake_mount', end: 'front' },
    compare: caliperOnMount,
  },
  {
    topic: 'Caliper mount',
    a: { categories: ['brake'], key: 'caliper_mount', only: 'rear' },
    b: { categories: ['frame'], key: 'rear_brake_mount', end: 'rear' },
    compare: caliperOnMount,
  },
  {
    topic: 'Brake pads',
    a: { categories: ['brake_pads'], key: 'pad_shape' },
    b: { categories: ['brake'], key: 'pad_shape' },
    compare: sameText('Pad shape'),
  },
  {
    topic: 'Tyre clearance',
    a: { categories: ['tyre'], key: 'width', only: 'rear' },
    b: { categories: ['frame'], key: 'max_tyre_width', end: 'rear' },
    compare: tyreClearance,
  },
  {
    topic: 'Tubeless',
    a: { categories: ['tyre'], key: 'setup' },
    b: { categories: ['wheel', 'rim'], key: 'tubeless_ready' },
    compare: tubeless,
  },
]

/**
 * Parts with no counterpart on another kind of part are compared with the
 * one they would replace (same category, same end).
 */
const REPLACES: Partial<
  Record<ComponentCategory, readonly { key: string; level: 'conflict' | 'caution'; why?: string }[]>
> = {
  shock: [
    { key: 'eye_to_eye', level: 'conflict' },
    {
      key: 'stroke',
      level: 'caution',
      why: 'A different stroke changes the travel; check the frame allows it.',
    },
    { key: 'mount', level: 'conflict' },
  ],
  chainring: [{ key: 'mount', level: 'caution', why: 'Check it suits your crank.' }],
  pedals: [{ key: 'thread', level: 'conflict' }],
}

// ---------------------------------------------------------------------------

function label(category: ComponentCategory, key: string): string {
  return specFields[category].find((f) => f.key === key)?.label ?? key
}

const present = (v: unknown) => v !== undefined && v !== null && v !== ''

/** Matches a part against a rule side, returning the end it is at (or null if it does not qualify). */
function onSide(part: CompatPart, side: Side): { end: End | undefined } | null {
  if (!side.categories.includes(part.category)) return null
  const end = endOf(part, side.end)
  if (side.only && end !== side.only) return null
  return { end }
}

const endsMeet = (x: End | undefined, y: End | undefined) =>
  x === undefined || y === undefined || x === y

/** How a part is referred to in messages, e.g. "the fork (Fox 36)". */
function refer(part: CompatPart, isCandidate: boolean): string {
  if (isCandidate) return `the new ${componentCategoryLabel[part.category].toLowerCase()}`
  const cat = componentCategoryLabel[part.category].toLowerCase()
  const end =
    POSITIONAL.has(part.category) && (part.position === 'front' || part.position === 'rear')
      ? `${part.position} `
      : ''
  return part.name && part.name.toLowerCase() !== cat
    ? `your ${end}${cat} (${part.name})`
    : `your ${end}${cat}`
}

/**
 * Check a candidate part against the parts fitted to a bike. Parts of the
 * same kind at the same end are the ones it would replace, so they are left
 * out of the pairwise checks.
 */
export function checkCompatibility(
  candidate: CompatPart,
  fitted: readonly CompatPart[],
): CompatResult {
  const candEnd = endOf(candidate)
  const replaced = fitted.filter(
    (p) => p.category === candidate.category && endsMeet(endOf(p), candEnd),
  )
  const others = fitted.filter((p) => !replaced.includes(p))
  const checks: CompatCheck[] = []

  for (const rule of RULES) {
    for (const [mine, theirs] of [
      [rule.a, rule.b],
      [rule.b, rule.a],
    ] as const) {
      const me = onSide(candidate, mine)
      if (!me) continue
      for (const part of others) {
        const them = onSide(part, theirs)
        if (!them || !endsMeet(me.end, them.end)) continue
        const mineValue = candidate.specs[mine.key]
        const theirValue = part.specs[theirs.key]
        const against = { id: part.id, name: part.name }
        if (!present(mineValue) || !present(theirValue)) {
          const missing = !present(mineValue)
            ? `“${label(candidate.category, mine.key)}” for the new part`
            : `“${label(part.category, theirs.key)}” for ${refer(part, false)}`
          checks.push({
            topic: rule.topic,
            status: 'unknown',
            message: `Can't tell yet: add ${missing}.`,
            against,
          })
          continue
        }
        const names = { a: '', b: '' }
        const candName = refer(candidate, true)
        const partName = refer(part, false)
        // Keep the rule's A/B order so comparisons stay one-directional.
        const [aVal, bVal] = mine === rule.a ? [mineValue, theirValue] : [theirValue, mineValue]
        names.a = mine === rule.a ? candName : partName
        names.b = mine === rule.a ? partName : candName
        const out = rule.compare(aVal, bVal, names)
        checks.push({ topic: rule.topic, ...out, message: sentence(out.message), against })
      }
    }
  }

  for (const rule of REPLACES[candidate.category] ?? []) {
    for (const part of replaced) {
      const a = candidate.specs[rule.key]
      const b = part.specs[rule.key]
      const topic = label(candidate.category, rule.key)
      const against = { id: part.id, name: part.name }
      if (!present(a) || !present(b)) {
        checks.push({
          topic,
          status: 'unknown',
          message: `Can't tell yet: add “${topic}” to both parts.`,
          against,
        })
      } else if (a === b) {
        checks.push({
          topic,
          status: 'ok',
          message: `Same ${topic.toLowerCase()} as ${refer(part, false)}: ${str(a)}.`,
          against,
        })
      } else {
        const message = `${topic} is ${str(a)}, but ${refer(part, false)} is ${str(b)}.${rule.why ? ` ${rule.why}` : ''}`
        checks.push({ topic, status: rule.level, message, against })
      }
    }
  }

  return { verdict: verdictOf(checks), checks: order(checks) }
}

function verdictOf(checks: readonly CompatCheck[]): CompatVerdict {
  if (checks.some((c) => c.status === 'conflict')) return 'wont_fit'
  if (checks.some((c) => c.status === 'caution')) return 'caution'
  if (checks.some((c) => c.status === 'ok')) return 'fits'
  return 'unknown'
}

const RANK: Record<CheckStatus, number> = { conflict: 0, caution: 1, unknown: 2, ok: 3 }
const order = (checks: CompatCheck[]) => [...checks].sort((x, y) => RANK[x.status] - RANK[y.status])

/** Capitalise the first letter; messages often start with "the" or "your". */
const sentence = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export const compatVerdictLabel: Record<CompatVerdict, string> = {
  fits: 'Fits',
  caution: 'Fits, with a catch',
  wont_fit: "Won't fit",
  unknown: "Can't tell yet",
}

/** Categories with at least one fit check, in the order the build sheet uses. */
export const checkableCategories: readonly ComponentCategory[] = (() => {
  const set = new Set<ComponentCategory>(Object.keys(REPLACES) as ComponentCategory[])
  for (const r of RULES) for (const c of [...r.a.categories, ...r.b.categories]) set.add(c)
  return (Object.keys(specFields) as ComponentCategory[]).filter((c) => set.has(c))
})()
